"use client";

import { type ReactNode, useEffect, useId } from "react";

import { clsx } from "../../utils/clsx";
import { transformChildrenToString } from "../../utils/transform-children-to-text";
import { useModalAriaContext } from "./modal-aria-context";

interface ModalBannerProps {
  title: ReactNode;
  titleClassName?: string;
  multilineTitle?: boolean;
  subtitle?: ReactNode;
  icon?: ReactNode;
  iconClassName?: string;
  centered?: boolean;
  children?: ReactNode;
}

export const ModalBanner = ({
  title,
  titleClassName,
  multilineTitle,
  subtitle,
  icon,
  iconClassName,
  centered,
  children,
}: ModalBannerProps) => {
  const headerId = useId();
  const { setLabelledById } = useModalAriaContext();

  useEffect(() => {
    setLabelledById(headerId);
  }, [headerId, setLabelledById]);

  return (
    <div className={clsx("flex shrink-0 items-start gap-6", centered && "items-center")}>
      {icon && <div className={clsx("relative grid size-16 shrink-0 place-items-center", iconClassName)}>{icon}</div>}

      <div className={clsx("flex grow flex-col justify-center gap-1.5 overflow-hidden", icon && "pt-2 md:pt-1")}>
        {subtitle && (
          <span
            className={clsx(
              "max-w-[90%] min-w-0 items-center gap-2 truncate text-sm leading-none text-contrast-600 uppercase",
              typeof subtitle !== "string" && "flex items-center gap-2",
            )}
            title={transformChildrenToString(subtitle)}
          >
            {subtitle}
          </span>
        )}
        <h1
          id={headerId}
          className={clsx(
            "max-w-[90%] text-h2 leading-tight uppercase md:text-h1",
            multilineTitle ? "line-clamp-2" : "truncate",
            titleClassName,
          )}
          title={transformChildrenToString(title)}
        >
          {title}
        </h1>

        {children}
      </div>
    </div>
  );
};
